import { useState, useEffect, useRef } from "react";
import { Send, CheckCircle2 } from "lucide-react";
import { useChat } from "../context/ChatContext";
import ContactActions from "./ContactActions";

export default function ChatConversation({ variant = "inline" }) {
  const { messages, loading, send, start, sessionId, captured } = useChat();
  const [input, setInput] = useState("");
  const listRef = useRef(null);

  useEffect(() => {
    if (!sessionId) start();
  }, [sessionId, start]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  const submit = (e) => {
    e.preventDefault();
    send(input);
    setInput("");
  };

  const heightClass = variant === "modal" ? "h-[60vh] md:h-[26rem]" : "h-[28rem]";

  return (
    <div className="flex flex-col" data-testid={`chat-conversation-${variant}`}>
      <div className="flex items-center gap-3 border-b border-white/10 px-4 py-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#3DDC97] text-[11px] font-extrabold text-[#0E0F12]">
          BA
        </div>
        <div>
          <p className="text-sm font-semibold text-white">BA Media Assistent</p>
          <p className="text-xs text-[#3DDC97]">Online · Anfragen gehen direkt an unser Team</p>
        </div>
      </div>

      <div ref={listRef} className={`${heightClass} space-y-3 overflow-y-auto px-4 py-4`} data-testid={`chat-messages-${variant}`}>
        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                m.role === "user"
                  ? "rounded-br-sm bg-[#3DDC97] text-[#0E0F12]"
                  : "rounded-bl-sm bg-[#1F222A] text-gray-100"
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="flex gap-1 rounded-2xl rounded-bl-sm bg-[#1F222A] px-4 py-3">
              <span className="h-2 w-2 animate-bounce rounded-full bg-[#3DDC97] [animation-delay:-0.3s]" />
              <span className="h-2 w-2 animate-bounce rounded-full bg-[#3DDC97] [animation-delay:-0.15s]" />
              <span className="h-2 w-2 animate-bounce rounded-full bg-[#3DDC97]" />
            </div>
          </div>
        )}
      </div>

      {captured && (
        <div className="flex items-center gap-2 border-t border-[#3DDC97]/20 bg-[#3DDC97]/10 px-4 py-2 text-xs text-[#3DDC97]">
          <CheckCircle2 size={14} /> Ihre Anfrage wurde aufgenommen — wir melden uns zeitnah.
        </div>
      )}

      <form onSubmit={submit} className="flex items-center gap-2 border-t border-white/10 p-3">
        <input
          data-testid={`chat-input-${variant}`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ihre Antwort…"
          className="flex-1 rounded-md border border-white/10 bg-[#0E0F12] px-3 py-2.5 text-sm text-white placeholder:text-gray-500 focus:border-[#3DDC97] focus:outline-none"
        />
        <button
          data-testid={`chat-send-btn-${variant}`}
          type="submit"
          disabled={loading || !input.trim()}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#3DDC97] text-[#0E0F12] transition-colors hover:bg-[#2BBF7E] disabled:opacity-40"
        >
          <Send size={16} />
        </button>
      </form>

      <p className="px-4 pb-2 text-center text-[11px] text-gray-500">
        Kein Formular-Marathon · alle Fragen werden an unser Team weitergeleitet
      </p>
      <div className="border-t border-white/10 p-3">
        <ContactActions size="sm" className="justify-center" />
      </div>
    </div>
  );
}
